import { Button } from 'primereact/button'
import { Card } from 'primereact/card'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import { PermissionSuffix, VatRecordDto } from '../../_generated/api/user-management'
import Organization from '../../models/Organization'
import t from '../../tools/t'
import ArchivedStateLabel from '../common/pure/ArchivedStateLabel'
import { ContentColumn, LayoutColumnContainer, LayoutRowContainer } from '../common/pure/layout'
import { Column, Row } from '../common/pure/FlexContainer'
import { TextField } from '../common/pure/input-fields'
import Heading from '../common/pure/Heading'
import FullScreenLoading from '../common/FullScreenLoading'
import LoadingCloak from '../common/pure/LoadingCloak'
import OrganizationAssociationList from '../common/pure/OrganizationAssociationList'
import { ErrorMessageScreen } from '../common/pure/MessageScreen'
import { useToastContext } from '../contexts/ToastContext'
import { useUserContext } from '../contexts/UserContext'
import { pi } from '../common/pure/PrimeIcon'

export default function OrganizationProfileScreen() {
    const { orgProfileId } = useParams()
    const {
        me,
        organizationsLoading,
        organizationsError,
        allOrganizationsLoading,
        updateOrganizationLoading,
        updateOrganization,
        restoreOrganization,
        getOrgProfileById,
    } = useUserContext()
    const Toast = useToastContext()
    const canWriteOrganizations = !!me?.hasAdminPermission(PermissionSuffix.TenantWrite)
    const [selectedOrganizationProfile, setSelectedOrganizationProfile] = useState(
        getOrgProfileById(orgProfileId)
    )
    const [name, setName] = useState(selectedOrganizationProfile?.name ?? '')

    const associations = selectedOrganizationProfile?.associations ?? []
    const [records, setRecords] = useState<VatRecordDto[]>(
        associations.length > 0 ? associations : [Organization.getEmptyVatRecord()]
    )

    const organizationProfileLoading = organizationsLoading || allOrganizationsLoading
    const isArchived = !!selectedOrganizationProfile?.isArchived

    useEffect(() => {
        if (!selectedOrganizationProfile) return
        setName(selectedOrganizationProfile.name)
        setRecords(
            selectedOrganizationProfile.associations.length > 0
                ? selectedOrganizationProfile.associations
                : [Organization.getEmptyVatRecord()]
        )
    }, [selectedOrganizationProfile])

    useEffect(() => {
        setSelectedOrganizationProfile(getOrgProfileById(orgProfileId))
    }, [orgProfileId, getOrgProfileById])

    const submitOrganizationUpdate = async () => {
        if (!selectedOrganizationProfile || isArchived) return
        const org = await updateOrganization(selectedOrganizationProfile.id, {
            name: name,
            vatRecords: records?.filter(Organization.isVatRecordValid) ?? [],
        })
        if (!org) return
        Toast.completed('change', 'organization')
        setSelectedOrganizationProfile(org)
    }

    const onRestoreOrganization = async () => {
        if (!selectedOrganizationProfile) return
        await restoreOrganization(selectedOrganizationProfile.id)
    }

    if (!selectedOrganizationProfile) {
        if (organizationProfileLoading) return <FullScreenLoading />
        return (
            <ErrorMessageScreen
                title={organizationsError?.name}
                message={organizationsError?.message}
            />
        )
    }

    return (
        <LayoutColumnContainer>
            <Row arrangement="spaced" alignment="center">
                <Column grow>
                    {isArchived && (
                        <ArchivedStateLabel
                            onRestore={canWriteOrganizations ? onRestoreOrganization : undefined}
                        />
                    )}
                    <Heading size={2}>{selectedOrganizationProfile.name}</Heading>
                </Column>

                {!isArchived && canWriteOrganizations && (
                    <Button
                        icon={pi('save')}
                        label={t('common_save_all')}
                        onClick={submitOrganizationUpdate}
                        disabled={isArchived}
                    />
                )}
            </Row>
            <LayoutRowContainer>
                <ContentColumn columns={1}>
                    <Card title={t('common_details')}>
                        <Column gap={0.5}>
                            <TextField
                                placeholder={t('user_field_full_name')}
                                value={name}
                                disabled={!canWriteOrganizations || isArchived}
                                onChange={(e) => setName(e.target.value)}
                            />
                        </Column>
                    </Card>
                </ContentColumn>
                <ContentColumn columns={2}>
                    <Card title={t('organization_field_association')}>
                        <OrganizationAssociationList
                            associations={records}
                            setAssociations={setRecords}
                            disabled={!canWriteOrganizations || isArchived}
                        />
                        <LoadingCloak loading={updateOrganizationLoading} />
                    </Card>
                </ContentColumn>
            </LayoutRowContainer>
        </LayoutColumnContainer>
    )
}
